import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import clsx from 'clsx'
import formatStaticAppText from 'lib/format/formatStaticAppText'
import OfferCardCarouselSection, {
  type OfferCardCarouselSectionFilterChangeMeta,
  OFFER_CARD_CAROUSEL_SECTION_NESTED_CLASS,
  MIN_OFFER_CAROUSEL_OFFERS_COUNT,
} from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import {
  useLocalDestinationOfferCarousel,
  type LocalDestinationOfferCarouselFilterOption,
  type CountryDestination,
} from 'home/hooks/useLocalDestinationOfferCarousel'
import OfferListEventsContext, {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import {
  buildLocalDestinationOfferAllFilters,
  isLocalDestinationOfferAllFilter,
} from './localDestinationOfferCarouselConstants'
import {
  buildLocalDestinationOfferCarouselAnalyticsData,
  LOCAL_DESTINATION_OFFER_CAROUSEL_CHIP_CHANGE_SUBJECT,
  LOCAL_DESTINATION_OFFER_CAROUSEL_MODULE_ID,
  resolvePendingLocalDestinationChipAnalyticsDestinationId,
  shouldDispatchLocalDestinationChipChangeAnalytics,
} from './localDestinationOfferCarouselAnalytics'
import { shouldRenderLocalDestinationOfferCarouselBody } from './localDestinationOfferCarouselModel'
import {
  buildLocalDestinationViewAllUrl,
  getEuropeanCountryDestinations,
  isLocalDestinationOfferFilterSelectionValid,
} from './localDestinationOfferCarouselUtils'
import { CAROUSEL_IN_VIEW_PARAMS } from 'home/constants'

interface Props {
  regionCode: string
  onListEvent: OfferListEventHandler
  excludedIds?: Array<string>
}

interface CarouselBodyProps {
  destinations: Array<CountryDestination>
  destinationFilterOptions: Array<LocalDestinationOfferCarouselFilterOption>
  excludedIds: Array<string> | undefined
  defaultDestinationId: string
  selectedFilter: string
  onSelectedFilterChange: (destinationId?: string) => void
}

function LocalDestinationOfferCarouselBody(props: CarouselBodyProps) {
  const {
    destinations,
    destinationFilterOptions,
    excludedIds,
    defaultDestinationId,
    selectedFilter,
    onSelectedFilterChange,
  } = props
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const intl = useIntl()
  const pendingUserChipAnalyticsDestinationIdRef = useRef<string | undefined>(undefined)

  const filters = useMemo(() => {
    return buildLocalDestinationOfferAllFilters()
  }, [])

  const europeDestinationName = useMemo(() => formatStaticAppText(intl, 'Europe'), [intl])

  const buildAllLink = useCallback((selectedFilter?: string) => {
    return buildLocalDestinationViewAllUrl(
      destinations,
      selectedFilter,
      europeDestinationName,
      intl,
    )
  }, [destinations, europeDestinationName, intl])

  const handleSelectedFilterChange = useCallback((
    value?: string,
    meta?: OfferCardCarouselSectionFilterChangeMeta,
  ) => {
    pendingUserChipAnalyticsDestinationIdRef.current = resolvePendingLocalDestinationChipAnalyticsDestinationId(
      value,
      selectedFilter,
      meta?.source,
    )
    onSelectedFilterChange(value)
  }, [onSelectedFilterChange, selectedFilter])

  useEffect(() => {
    const pendingDestinationId = pendingUserChipAnalyticsDestinationIdRef.current
    if (!shouldDispatchLocalDestinationChipChangeAnalytics(selectedFilter, pendingDestinationId)) {
      return
    }

    pendingUserChipAnalyticsDestinationIdRef.current = undefined

    dispatchOfferListEvent({
      type: OfferListEvents.clientAction,
      subject: LOCAL_DESTINATION_OFFER_CAROUSEL_CHIP_CHANGE_SUBJECT,
      action: 'selected',
      key: EventDataKey.ClientAction,
    })
  }, [dispatchOfferListEvent, selectedFilter])

  const title = useMemo(() => (
    <FormattedMessage
      id="FJ8n_pZRh3dpgXThzj8f"
      defaultMessage="Explore <i>European</i> hotel offers"
      description="Heading for the Europe-only hotel offer carousel below Trending Destinations on European homepages"
      values={{
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      }}
    />
  ), [])

  const lookingForSomethingElseTo = useMemo(() => buildAllLink(selectedFilter), [buildAllLink, selectedFilter])

  return (
    <OfferCardCarouselSection
      filters={filters}
      title={title}
      additionalFilters="destinations"
      additionalFilterOptions={destinationFilterOptions}
      maxFilterOptions={destinationFilterOptions.length}
      selectedFilter={selectedFilter}
      defaultFilter={defaultDestinationId}
      buildAllLink={buildAllLink}
      onSelectedFilterChange={handleSelectedFilterChange}
      shouldKeepBaseDestinationFilters={isLocalDestinationOfferAllFilter}
      resetFilterWhenSelectionInvalid
      className={clsx(OFFER_CARD_CAROUSEL_SECTION_NESTED_CLASS)}
      tileStyle="card-default"
      tileSize="medium"
      minimumOffersCount={MIN_OFFER_CAROUSEL_OFFERS_COUNT}
      excludedIds={excludedIds}
      data-testid={LOCAL_DESTINATION_OFFER_CAROUSEL_MODULE_ID}
    >
      {lookingForSomethingElseTo && <OfferCarouselLookingForSomethingElse to={lookingForSomethingElseTo} />}
    </OfferCardCarouselSection>
  )
}

function LocalDestinationOfferCarousel(props: Props) {
  const { regionCode, onListEvent, excludedIds } = props
  const [inViewRef, inView] = useInView(CAROUSEL_IN_VIEW_PARAMS)
  const europeanCountryDestinations = useMemo(() => getEuropeanCountryDestinations(regionCode), [regionCode])

  const {
    countryDestinationsWithIds,
    destinations,
    destinationFilterOptions,
    defaultDestinationId,
    checkingEligibility,
    allChecksComplete,
  } = useLocalDestinationOfferCarousel(europeanCountryDestinations, { enabled: inView })
  const [selectedDestinationId, setSelectedDestinationId] = useState<string | undefined>()

  useEffect(() => {
    if (!inView) {
      return
    }

    setSelectedDestinationId((current) => {
      if (current === undefined) {
        return current
      }

      if (isLocalDestinationOfferFilterSelectionValid(current, destinations)) {
        return current
      }

      return undefined
    })
  }, [destinations, inView])

  const handleSelectedDestinationChange = useCallback((destinationId?: string) => {
    setSelectedDestinationId(destinationId)
  }, [])

  const activeDestinationId = (
    selectedDestinationId
    && isLocalDestinationOfferFilterSelectionValid(selectedDestinationId, destinations)
      ? selectedDestinationId
      : undefined
  ) ?? defaultDestinationId

  const analyticsAdditionalData = useMemo(() => {
    return buildLocalDestinationOfferCarouselAnalyticsData({
      marketCode: regionCode,
      countryDestinations: countryDestinationsWithIds,
      eligibleDestinations: destinations,
      selectedDestinationId: activeDestinationId,
      defaultDestinationId,
    })
  }, [
    activeDestinationId,
    defaultDestinationId,
    destinations,
    regionCode,
    countryDestinationsWithIds,
  ])

  const shouldRenderCarouselBody = shouldRenderLocalDestinationOfferCarouselBody({
    defaultDestinationId,
    allChecksComplete,
    eligibleDestinationCount: destinations.length,
  })

  let content: React.ReactNode = undefined

  if (!inView) {
    // Sentinel only: keep eligibility fetches off the critical path until near viewport.
    content = undefined
  } else if (shouldRenderCarouselBody && defaultDestinationId) {
    content = (
      <OfferListEventsProvider onListEvent={onListEvent} additionalData={analyticsAdditionalData}>
        <LocalDestinationOfferCarouselBody
          destinations={destinations}
          destinationFilterOptions={destinationFilterOptions}
          excludedIds={excludedIds}
          defaultDestinationId={defaultDestinationId}
          selectedFilter={activeDestinationId ?? defaultDestinationId}
          onSelectedFilterChange={handleSelectedDestinationChange}
        />
      </OfferListEventsProvider>
    )
  }

  return (
    <div ref={inViewRef} aria-busy={inView && checkingEligibility}>
      {content}
    </div>
  )
}

export default React.memo(LocalDestinationOfferCarousel)
